/**
 * @Author: Karen Yunuen Pozos Arias
 * @JobTitle: Full Stack Developer - IBL
 * @Email: kpozos@ibl.mx
 */
// Importing necessary dependencies for translations, state management, and file exports
import { useTranslation } from "react-i18next"; // for handling translations
import { useEffect, useState } from "react"; // React hooks for component state and lifecycle
import { useSelector } from "react-redux"; // Redux hooks for dispatching actions and accessing state
import HandleExportApp from "./HandleExportApp"; // Import error codes for handling errors

// Component to handle file exports (PDF/Excel) with dynamic headers and user filtering
const ExportApp = ({ onHide, option, body, searchElement, moduleId, exportAttendance, search, contractorSupplier }) => {
    const { t } = useTranslation(); // Translation hook
    const optionsCheckbox = useSelector((state) => state.FilesSlice.getExcelHeadersByOption); // Accessing available header options from Redux state
    const checkboxes = optionsCheckbox?.data || []; // Default to empty array if no checkboxes are available
    const [selectedOptions, setSelectedOptions] = useState([]); // State to store selected header options
    const [selectAll, setSelectAll] = useState(true); // State to track if all checkboxes are selected

    // Handles the change of a specific checkbox (add/remove from selected options)
    const handleCheckboxChange = (checkboxId, checkboxName) => {
        const isChecked = selectedOptions.includes(checkboxName); // Check if checkbox is already selected
        if (isChecked) {
            setSelectedOptions(selectedOptions.filter((name) => name !== checkboxName)); // Remove from selected options if unchecked
        } else {
            setSelectedOptions([...selectedOptions, checkboxName]); // Add to selected options if checked
        }
        if (selectedOptions.length === checkboxes.length - 1 && !isChecked) {
            setSelectAll(true); // If all checkboxes except the current one are selected, set "select all" to true
        } else {
            setSelectAll(false); // Otherwise, set "select all" to false
        }
    };

    // On component mount or checkboxes change, select all available options
    useEffect(() => {
        if (checkboxes.length > 0) {
            const allOptions = checkboxes.map((checkbox) => checkbox.name); // Map all checkboxes to their names
            setSelectedOptions(allOptions); // Set all options as selected
        }
    }, [checkboxes]);

    // Handles the "select all" checkbox behavior
    const handleSelectAllChange = () => {
        if (selectAll) {
            setSelectedOptions([]); // Deselect all options if "select all" is checked
        } else {
            const allOptions = checkboxes.map((checkbox) => checkbox.name); // Map all checkboxes to their names
            setSelectedOptions(allOptions); // Select all options
        }
        setSelectAll(!selectAll); // Toggle the "select all" state
    };

    // Clear the selected options (deselect all)
    const clearSelectedOptions = () => {
        setSelectedOptions([]); // Reset selected options to an empty array
        setSelectAll(false); // Reset "select all" state
    };

    const {
        handleDownloadExcelAttendance,
        handleDownloadExcel,
        handleDownloadPDFAttendance,
        handleDownloadPDF
    } = HandleExportApp({
        clearSelectedOptions,
        selectedOptions,
        body,
        search,
        onHide,
        moduleId,
        option,
        exportAttendance,
        searchElement,
        contractorSupplier
    });

    // Returning all necessary handlers and state for use in the component
    return {
        t,
        checkboxes,
        selectedOptions,
        selectAll,
        handleDownloadExcelAttendance,
        handleDownloadExcel,
        handleDownloadPDFAttendance,
        handleDownloadPDF,
        handleSelectAllChange,
        handleCheckboxChange
    };
};

// Exporting the component for use in other parts of the application
export default ExportApp;
